import { useCallback, useEffect, useMemo, useState } from 'react'
import { Images, Loader2, ChevronLeft, X, Settings2, Eye, EyeOff, Search, ChevronRight } from 'lucide-react'
import type { ImmichAlbumDto, ImmichAssetDto, ImmichManageAlbumDto } from '@familynest/shared'
import { AuthImage } from '../AuthImage'
import { api } from '../../lib/api'
import { useAuth } from '../../store/auth'
import { Button, useBackdropClose } from '../ui'

/**
 * Galerie — read-only Immich-Anbindung über den Backend-Proxy (ADR-007).
 * Erwachsene verlinken Alben; alle sehen die verlinkten Alben und öffnen Fotos
 * in der Lightbox. Bilder laufen ausschließlich über /api/integrations/immich/…
 */
export function GalleryScreen(): React.JSX.Element {
  const { member } = useAuth()
  const isAdult = member?.role === 'adult'
  const [configured, setConfigured] = useState<boolean | null>(null)
  const [albums, setAlbums] = useState<ImmichAlbumDto[]>([])
  const [openAlbum, setOpenAlbum] = useState<ImmichAlbumDto | null>(null)
  const [manage, setManage] = useState(false)

  const loadAlbums = useCallback(async () => {
    const st = await api.integrationsStatus()
    setConfigured(st.immich)
    if (st.immich) setAlbums(await api.immichAlbums())
  }, [])

  useEffect(() => {
    void loadAlbums()
  }, [loadAlbums])

  if (configured === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-6 animate-spin" style={{ color: 'var(--color-muted)' }} />
      </div>
    )
  }

  if (!configured) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center" style={{ color: 'var(--color-muted)' }}>
        <Images className="size-8" />
        <p className="max-w-sm text-sm">Die Galerie ist noch nicht eingerichtet (Immich-Zugang fehlt serverseitig).</p>
      </div>
    )
  }

  if (openAlbum) {
    return <AlbumView album={openAlbum} onBack={() => setOpenAlbum(null)} />
  }

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 md:p-4">
      <div className="flex items-center gap-2">
        <h1 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>Galerie</h1>
        {isAdult && (
          <Button variant="ghost" className="ml-auto" onClick={() => setManage(true)}>
            <Settings2 className="size-4" /> Alben verwalten
          </Button>
        )}
      </div>

      {albums.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm" style={{ color: 'var(--color-muted)' }}>
          <Images className="size-8" />
          Keine Alben mit deinem Immich-Konto geteilt (oder alle ausgeblendet).
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
          {albums.map((a) => (
            <button key={a.albumId} onClick={() => setOpenAlbum(a)} className="group flex w-full flex-col overflow-hidden rounded-[var(--radius-card)] border text-left" style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-card)' }}>
              {/* Quadratisches Cover per padding-bottom (funktioniert in jedem Layout). */}
              <div className="relative w-full overflow-hidden" style={{ paddingBottom: '100%', backgroundColor: 'var(--color-surface)' }}>
                {a.coverUrl ? (
                  <AuthImage src={a.coverUrl} alt="" loading="lazy" className="absolute inset-0 size-full object-cover transition-transform group-hover:scale-105" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center"><Images className="size-8" style={{ color: 'var(--color-muted)' }} /></div>
                )}
              </div>
              <div className="p-2">
                <div className="truncate text-sm font-medium" style={{ color: 'var(--color-ink)' }}>{a.title}</div>
                <div className="text-xs" style={{ color: 'var(--color-muted)' }}>{a.assetCount} {a.assetCount === 1 ? 'Foto' : 'Fotos'}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {manage && <ManageDialog onClose={() => setManage(false)} onChanged={loadAlbums} />}
    </div>
  )
}

function AlbumView({ album, onBack }: { album: ImmichAlbumDto; onBack: () => void }): React.JSX.Element {
  const [assets, setAssets] = useState<ImmichAssetDto[] | null>(null)
  const [title, setTitle] = useState(album.title)
  const [lightbox, setLightbox] = useState<number | null>(null)

  useEffect(() => {
    let alive = true
    void api.immichAlbumAssets(album.albumId).then((r) => {
      if (!alive) return
      setAssets(r.assets)
      setTitle(r.album.title)
    })
    return () => {
      alive = false
    }
  }, [album.albumId])

  return (
    <div className="flex min-h-full flex-col">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b px-3 py-3" style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-card)' }}>
        <button onClick={onBack} className="rounded-lg p-1 hover:bg-black/5" aria-label="Zurück">
          <ChevronLeft className="size-5" style={{ color: 'var(--color-ink)' }} />
        </button>
        <h2 className="truncate text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>{title}</h2>
        {assets && <span className="text-sm" style={{ color: 'var(--color-muted)' }}>{assets.length}</span>}
      </div>

      {!assets ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="size-6 animate-spin" style={{ color: 'var(--color-muted)' }} /></div>
      ) : assets.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-sm" style={{ color: 'var(--color-muted)' }}>Dieses Album ist leer.</div>
      ) : (
        <div className="grid grid-cols-3 gap-1 p-1 sm:grid-cols-4 md:grid-cols-6">
          {assets.map((a, i) => (
            <button key={a.id} onClick={() => setLightbox(i)} className="relative w-full overflow-hidden" style={{ paddingBottom: '100%', backgroundColor: 'var(--color-surface)' }}>
              <AuthImage src={a.thumbUrl} alt="" loading="lazy" className="absolute inset-0 size-full object-cover" />
              {a.type === 'video' && (
                <span className="absolute bottom-1 right-1 rounded bg-black/60 px-1 text-[10px] text-white">▶</span>
              )}
            </button>
          ))}
        </div>
      )}

      {lightbox !== null && assets && (
        <Lightbox assets={assets} index={lightbox} onClose={() => setLightbox(null)} onNav={(i) => setLightbox(i)} />
      )}
    </div>
  )
}

function Lightbox({ assets, index, onClose, onNav }: { assets: ImmichAssetDto[]; index: number; onClose: () => void; onNav: (i: number) => void }): React.JSX.Element {
  const a = assets[index]!
  const prev = (): void => onNav((index - 1 + assets.length) % assets.length)
  const next = (): void => onNav((index + 1) % assets.length)

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft') prev()
      else if (e.key === 'ArrowRight') next()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90" {...backdrop}>
      <button onClick={onClose} className="absolute right-3 top-3 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" aria-label="Schließen">
        <X className="size-6" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); prev() }} className="absolute left-2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" aria-label="Zurück">
        <ChevronLeft className="size-7" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); next() }} className="absolute right-2 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" aria-label="Weiter">
        <ChevronRight className="size-7" />
      </button>
      <div className="max-h-[92vh] max-w-[94vw]" onClick={(e) => e.stopPropagation()}>
        {a.type === 'video' ? (
          <video src={a.originalUrl} controls autoPlay className="max-h-[92vh] max-w-[94vw]" />
        ) : (
          <AuthImage src={a.previewUrl} alt="" className="max-h-[92vh] max-w-[94vw] object-contain" />
        )}
      </div>
      <span className="absolute bottom-3 left-1/2 -translate-x-1/2 text-sm text-white/70">{index + 1} / {assets.length}</span>
    </div>
  )
}

function ManageDialog({ onClose, onChanged }: { onClose: () => void; onChanged: () => Promise<void> }): React.JSX.Element {
  const [albums, setAlbums] = useState<ImmichManageAlbumDto[] | null>(null)
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setAlbums(await api.immichManageAlbums())
    } catch {
      setError('Immich nicht erreichbar.')
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const filtered = useMemo(() => {
    const list = albums ?? []
    const term = q.trim().toLowerCase()
    const f = term ? list.filter((a) => a.title.toLowerCase().includes(term)) : list
    return f.slice(0, 400)
  }, [albums, q])

  async function toggle(a: ImmichManageAlbumDto): Promise<void> {
    setBusy(a.albumId)
    setError(null)
    try {
      if (a.hidden) await api.immichUnhideAlbum(a.albumId)
      else await api.immichHideAlbum(a.albumId)
      await load()
      await onChanged()
    } catch {
      setError('Aktion fehlgeschlagen.')
    } finally {
      setBusy(null)
    }
  }

  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: 'var(--color-line)' }}>
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>Alben verwalten</h2>
            <p className="text-xs" style={{ color: 'var(--color-muted)' }}>Alle geteilten Alben sind sichtbar. Hier kannst du einzelne ausblenden.</p>
          </div>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5"><X className="size-5" style={{ color: 'var(--color-muted)' }} /></button>
        </div>
        <div className="px-5 py-3">
          <div className="flex items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-surface)' }}>
            <Search className="size-4" style={{ color: 'var(--color-muted)' }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Album suchen…" className="w-full bg-transparent outline-none" style={{ color: 'var(--color-ink)' }} />
          </div>
          {error && <p className="mt-2 text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
        </div>
        <div className="flex-1 overflow-auto px-2 pb-3">
          {albums === null ? (
            <div className="flex justify-center py-8"><Loader2 className="size-5 animate-spin" style={{ color: 'var(--color-muted)' }} /></div>
          ) : (
            filtered.map((a) => (
              <div key={a.albumId} className="flex items-center gap-3 rounded-lg px-3 py-2" style={{ opacity: a.hidden ? 0.5 : 1 }}>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm" style={{ color: 'var(--color-ink)' }}>{a.title}</span>
                  <span className="text-xs" style={{ color: 'var(--color-muted)' }}>{a.assetCount} Fotos{a.sharedWith.length ? ` · ${a.sharedWith.join(', ')}` : ''}</span>
                </span>
                <button
                  onClick={() => toggle(a)}
                  disabled={busy === a.albumId}
                  title={a.hidden ? 'Einblenden' : 'Ausblenden'}
                  className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs hover:bg-black/5"
                  style={{ color: a.hidden ? 'var(--color-accent)' : 'var(--color-muted)' }}
                >
                  {busy === a.albumId ? <Loader2 className="size-4 animate-spin" /> : a.hidden ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  {a.hidden ? 'Ausgeblendet' : 'Sichtbar'}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
